import { WeekPicker } from '@/components/dumb';
import { useTasks } from '@/features/tasks/model/useTaskActions';
import { createTask } from '@/entities/task/api/tasks';
import type { TaskApiPayload } from '@/features/tasks/model/forms/useTaskForm';
import TaskFormModal from '@/features/tasks/task-form-modal/TaskCreateModal';
import type { Task } from '@/shared/types/task';
import { useAuthStore } from '@/store/auth';
import { useTaskFiltersStore } from '@/entities/task/model/stores/task-filters';
import { useUIStore } from '@/app/model';
import { format, isSameDay, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { MeetingCalendarHeader } from '../meetingCalendarHeader';
import { SlotCreateModal, SlotsPanel } from '@/widgets/slot';
import { CollapsiblePanel } from './CollapsiblePanel';

type TabId = 'meetings' | 'slots';
const ANIM_MS = 300;

// Стекло — тот же рецепт, что у GlassPageHeader (шапка детальных страниц):
// заказчик попросил ровно такую же прозрачность. Блюр держим лёгким (1px) —
// сильный блюр смазывает то, что проходит под шапкой (сетку часов, карточки
// задач) в сплошную заливку и на плоском фоне календаря выглядит как
// непрозрачная плашка, а не стекло.
const HEADER_GLASS = {
  background: 'rgba(40, 56, 92, 0.9)',
  backdropFilter: 'blur(1px) saturate(250%)',
  WebkitBackdropFilter: 'blur(1px) saturate(250%)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.25)',
  boxShadow: '0 2px 12px rgba(30, 42, 74, 0.08)',
} as const;

const MEETINGS_ID = 6;

function toNumberArray(v: unknown): number[] {
  if (!Array.isArray(v)) {
    return [];
  }
  return v.map((x) => Number(x)).filter((n) => Number.isFinite(n));
}

function isParticipant(task: Task, uid: number) {
  const accomplices = toNumberArray(task.accomplices);
  const auditors = toNumberArray(task.auditors);

  return (
    task.assigneeId === uid ||
    task.createdBy === uid ||
    accomplices.includes(uid) ||
    auditors.includes(uid)
  );
}

export function MobileHeader() {
  const { headerBottomContent, setIsCalendarOpen } = useUIStore();

  const { selectedDate, setSelectedDate } = useTaskFiltersStore();
  const { userId } = useAuthStore();

  const [activeTab, setActiveTab] = useState<TabId | null>(null);
  const [renderTab, setRenderTab] = useState<TabId | null>(null);

  const [slotModalOpen, setSlotModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<30 | 60>(60);

  const handleSelectSlot = (slot: string, duration: 30 | 60) => {
    setSelectedSlot(slot);
    setSelectedDuration(duration);
    setSlotModalOpen(true);
  };

  const [isMeetingModalOpen, setIsMeetingModalOpen] = useState(false);
  const handleCreateMeeting = () => setIsMeetingModalOpen(true);
  const handleCloseMeetingModal = () => setIsMeetingModalOpen(false);

  const dateStr = useMemo(
    () => format(selectedDate, 'yyyy-MM-dd'),
    [selectedDate]
  );

  const uid = useMemo(() => {
    const n = Number(userId);
    return Number.isFinite(n) ? n : null;
  }, [userId]);

  const {
    data: tasksData,
    refetch: refetchTasks,
    isLoading,
    isFetching,
    isRefetching,
  } = useTasks(
    {
      page: 1,
      perPage: 200,
      dateFrom: dateStr,
      dateTo: dateStr,
      currentUserId: userId,
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: !!userId,
      refetchOnMount: true,
      staleTime: 2 * 60 * 1000,
    }
  );

  const loadingMeetings = isLoading || isFetching || isRefetching;

  const dayTasks = useMemo(() => {
    if (!uid) {
      return [];
    }
    const items = (tasksData?.items ?? []) as Task[];

    return items.filter((task) => {
      if (!task.dueDate) {
        return false;
      }
      const okDay = isSameDay(parseISO(task.dueDate), selectedDate);
      if (!okDay) {
        return false;
      }

      return isParticipant(task, uid);
    });
  }, [tasksData?.items, selectedDate, uid]);

  const meetingTasks = useMemo(() => {
    if (!uid) {
      return [];
    }
    return dayTasks.filter((t) => {
      const isMeeting = !!t.isMeeting || t.groupId === MEETINGS_ID;
      if (!isMeeting) {
        return false;
      }

      return isParticipant(t, uid);
    });
  }, [dayTasks, uid]);

  const handleMeetingSubmit = async (formData: TaskApiPayload) => {
    try {
      await createTask(formData);
      await refetchTasks();

      handleCloseMeetingModal();
      setIsCalendarOpen(true);
      setActiveTab('meetings');
      setRenderTab('meetings');
    } catch (error) {
      console.error('Ошибка при создании встречи:', error);
    }
  };

  const panelOpen = activeTab !== null;

  const closeTimerRef = useMemo(() => ({ id: 0 as number | 0 }), []);

  const handleTabChange = (next: TabId | null) => {
    if (closeTimerRef.id) {
      window.clearTimeout(closeTimerRef.id);
      closeTimerRef.id = 0;
    }

    if (next) {
      setRenderTab(next);
      setActiveTab(next);
      return;
    }

    setActiveTab(null);
    closeTimerRef.id = window.setTimeout(() => {
      setRenderTab(null);
      closeTimerRef.id = 0;
    }, ANIM_MS);
  };

  return (
    <>
      <header
        className={`sticky w-full top-0 z-[30] lg:hidden pt-[8px] flex flex-col ${
          panelOpen ? 'rounded-b-[30px]' : 'rounded-b-[10px]'
        } font-roboto`}
        style={{ maxHeight: '100dvh', ...HEADER_GLASS }}
      >
        <WeekPicker
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          variant="dark"
        />

        <div className="px-4 flex-1 min-h-0 overflow-hidden">
          <CollapsiblePanel
            isOpen={panelOpen}
            durationMs={ANIM_MS}
            maxHeightPercentage={80}
            lockBodyScroll={false}
            contentKey={renderTab ?? 'closed'}
          >
            {renderTab === 'meetings' && (
              <MeetingCalendarHeader
                isOpen={true}
                onCreateMeeting={handleCreateMeeting}
                tasks={meetingTasks}
                taskType="task"
                isLoading={loadingMeetings}
                maxHeightPercentage={80}
              />
            )}

            {renderTab === 'slots' && (
              <SlotsPanel
                tasks={dayTasks}
                selectedDate={selectedDate}
                onSelectSlot={handleSelectSlot}
                selectedSlot={selectedSlot}
                isLoading={loadingMeetings}
              />
            )}
          </CollapsiblePanel>
        </div>

        <div className="relative z-20 shrink-0 pt-[12px] pb-[12px]">
          <HeaderTabs activeTab={activeTab} onChange={handleTabChange} />
        </div>

        {headerBottomContent && (
          <div className="rounded-b-2xl shrink-0">{headerBottomContent}</div>
        )}

        <SlotCreateModal
          open={slotModalOpen}
          slot={selectedSlot}
          duration={selectedDuration}
          onClose={() => setSlotModalOpen(false)}
        />
      </header>

      <TaskFormModal
        open={isMeetingModalOpen}
        onClose={handleCloseMeetingModal}
        onSubmit={handleMeetingSubmit}
        mode="create"
        isLoading={false}
        initialData={{
          TITLE: 'Новая встреча',
          GROUP_ID: 6,
          DESCRIPTION: '',
          RESPONSIBLE_ID: undefined,
          DEADLINE: '',
          ACCOMPLICES: [],
          AUDITORS: [],
        }}
      />
    </>
  );
}

function HeaderTabs({
  activeTab,
  onChange,
}: {
  activeTab: TabId | null;
  onChange: (v: TabId | null) => void;
}) {
  const tabs = [
    { id: 'meetings', label: 'Встречи' },
    { id: 'slots', label: 'Слоты' },
  ] as const;

  return (
    <div className="flex justify-center gap-[138px] pb-[8px]">
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;

        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(isActive ? null : tab.id)}
            className={`relative text-sm transition-colors ${
              isActive ? 'text-white' : 'text-white/50'
            }`}
          >
            {tab.label}
            {isActive && (
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-[6px] h-[6px] rounded-full bg-white" />
            )}
          </button>
        );
      })}
    </div>
  );
}
